import { AuthContext } from 'contexts/AuthContext';
import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import { useContext, useEffect, useState } from 'react';

const SelectClass = ({
  setDisable,
  setLoading,
  classData,
  setClassData,
  courses,
  loadingCourses,
}) => {
  const [errors, setErrors] = useState([]);
  const { state } = useContext(AuthContext);

  const [batches, setBatches] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;

  const handleCourseChange = async (event) => {
    const courseId = event.target.value;

    setClassData({
      ...classData,
      courseId,
      structureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setBatches([]);

    if (!courseId) return;

    try {
      setLoadingStructures(true);
      const res = await batchTeacherStructureService.getBatchStructuresByCourse(
        courseId
      );
      if (res.data.success) {
        setBatchStructures(res.data.batchStructures || []);
      }
    } catch (err) {
      console.error('Error loading structures:', err);
    } finally {
      setLoadingStructures(false);
    }
  };

  const handleStructureChange = async (event) => {
    const structureId = event.target.value;
    setClassData({ ...classData, structureId, batchId: '' });
    setBatches([]);

    if (!structureId) return;

    try {
      setLoadingBatches(true);
      const res =
        await batchTeacherStructureService.getBatchesByCourseStructure(
          classData.courseId,
          structureId,
          selectedBranch
        );
      if (res.data.success) {
        setBatches(res.data.batches || []);
      }
    } catch (err) {
      console.error('Error loading batches:', err);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleBatchChange = (event) => {
    const batchId = event.target.value;
    setClassData({ ...classData, batchId });
  };

  useEffect(() => {
    const newErrors = [];
    if (
      !classData?.courseId ||
      !classData?.structureId ||
      !classData?.batchId
    ) {
      newErrors.push('Please fill in all fields.');
    }
    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [classData, setDisable]);

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      {/* Header */}
      <div className="flex justify-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Select Class Information
        </h4>
      </div>

      {/* Course Select */}
      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-center md:gap-6">
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            onChange={handleCourseChange}
            value={classData?.courseId}
            name="course"
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Course</option>
            {loadingCourses ? (
              <option>Loading courses...</option>
            ) : courses.length === 0 ? (
              <option className="cursor-pointer font-bold text-brand-500">
                No courses available
              </option>
            ) : (
              courses.map((course, i) => (
                <option key={course._id} value={course._id}>
                  {i + 1}. {course.courseName}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Structure Select */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Structure
          </label>
          <select
            value={classData?.structureId || ''}
            onChange={handleStructureChange}
            disabled={!classData.courseId}
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Select Structure</option>
            {loadingStructures ? (
              <option>Loading...</option>
            ) : batchStructures.length === 0 ? (
              <option>No structures available</option>
            ) : (
              batchStructures.map((bs) => (
                <option key={bs._id} value={bs._id}>
                  {bs.name}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Batch Select */}
        <div className="w-full md:w-1/3">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch
          </label>
          <select
            onChange={handleBatchChange}
            value={classData?.batchId}
            name="batch"
            disabled={!classData.structureId}
            className="mt-2 w-full md:min-w-[400px] rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Batch</option>
            {loadingBatches ? (
              <option>Loading...</option>
            ) : batches.length > 0 ? (
              batches.map((batch) => {
                const batchDisplay = `B${batch.batchNumber}`;
                
                const date = new Date(batch.startDate);
                const day = date.getDate().toString().padStart(2, '0');
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear().toString().slice(-2);
                const dateDisplay = `${day}${month}${year}`;

                const dayMap = {
                  Monday: 'M',
                  Tuesday: 'T',
                  Wednesday: 'W',
                  Thursday: 'Th',
                  Friday: 'F',
                  Saturday: 'S',
                  Sunday: 'Su',
                };

                const scheduleDisplay = batch.daySchedules
                  ?.map((schedule) => {
                    const dayShort = dayMap[schedule.day] || schedule.day;
                    const time = schedule.startTime.replace(':00', '').replace(' ', '');
                    return `${dayShort}-${time}`;
                  })
                  .join(' | ');

                const branchInitials = batch.branches
                  ?.map((br) => br.name.split(',').pop().trim()[0])
                  .join('');

                const schedulePart = scheduleDisplay ? `(${scheduleDisplay} )` : '';
                const fullDisplay = `${batchDisplay} | ${dateDisplay} ${schedulePart} ${branchInitials || ''}`.trim();

                return (
                  <option key={batch._id} value={batch._id}>
                    {fullDisplay}
                  </option>
                );
              })
            ) : (
              <option>No batches available</option>
            )}
          </select>
        </div>
      </div>
      {/* Display errors */}
      {errors.length > 0 && (
        <div className="text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectClass;
